/* eslint-disable max-lines */
import { goTo } from '@adornis/router/client/open-href.js';

import { logger } from '@adornis/base/logging.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { XButton } from '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import '@adornis/popover/x-dropdown.js';
import { openPopupWithLoadingScreen } from '@adornis/print/client/safe-popup.js';
import { servePdfFromComponent } from '@adornis/print/db/print.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import type { ILSThemeDefinition } from '@ils/theme/theme.js';
import { html, nothing, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { combineLatest, filter, map, of, startWith, switchMap, type Observable } from 'rxjs';
import { ExamDay } from '../../db/examdays.js';
import type { ILSUser } from '../../db/ils-user.js';
import {
  Order,
  createInvoice,
  manuallySetOrderPaid,
  manuallySetOrderRevoked,
  recreateInvoice,
  subscribeOrdersByUserID,
} from '../../db/order.js';
import { isDevelopment } from '../../shared/util.js';
import {
  generateInvoicePdfFileName,
  generatePdfUrl,
  generateRecreateInvoicePdfFileName,
  generateResultPdfFileName,
} from '../../standalone-components/PdfUtil.js';
// import { Appointment } from './../../db/appointment';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { getByID, subscribeByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import '@adornis/popover/x-tooltip.js';
import { sendCertificate } from '@ils/certificates/api/certificates.js';
import { examTypeBadge } from '@ils/examType/client/examTypeBadge.js';
import { permissionGuard } from '@ils/permissions/client/permissionGuard.js';
import { hasPermission } from '@ils/permissions/hasPermission.js';
import type { ILSUserOverviewLineOrder } from '@legacy-db/ils-user-overview-line.js';
import { ILSFile } from '../../db/ils-files.js';
import { ILSOrderCommentForm } from './forms/ils-order-comment-form.js';
import { ILSAdminCreateOrder } from './ils-admin-create-order.js';
import { ILSAdminMoveOrder } from './ils-admin-move-order.js';
import { orderStatusIcon } from './orderStatusIcon.js';
import { simpleInformationGrid } from './simple-information-grid.js';
import { squareIcon } from './squareIcon.js';

// To calc the best order state, order of the items is important!
const compareArr = ['issued', 'expired', 'canceled', 'revoked', 'failed', 'refund', 'paid', 'moved'];

const examDaySelector = {
  _id: 1,
  _class: 1,
  name: 1,
  candidateIDs: 1,
  examType: 1,
  start: 1,
  examCenter: 1,
  location: { displayName: 1 },
} as const satisfies BaseQLSelectionSet<ExamDay>;

type NarrowExamDay = NarrowedEntity<ExamDay, typeof examDaySelector>;

// attention: should be the same as in ils-users
const orderSelector = {
  _id: 1,
  status: 1,
  createdAt: 1,
  paymentType: 1,
  movedFromOrder: 1,
  processorInfo: { stripePaymentIntent: 1, stripeRefundID: 1 },
  userReference: { userID: 1 },
  comment: 1,
  products: { product: { _id: 1, reference: 1, name: 1, examType: 1 }, amount: 1 },
  isExamDayPassed: 1,
  isExamDayRated: 1,
  invoiceNumber: 1,
  orderNumber: 1,
  invoices: {
    status: 1,
    stripeID: 1,
    reference: 1,
    total: 1,
  },
  revokedAt: 1,
} as const satisfies BaseQLSelectionSet<Order>;

type NarrowOrder = NarrowedEntity<Order, typeof orderSelector>;

/**
 * @element ils-admin-order-detail-trigger
 */
@customElement('ils-admin-order-detail-trigger')
export class ILSAdminOrderDetail extends ChemistryLitElement<ILSThemeDefinition> {
  @property({ attribute: false }) user = new RXController<ILSUser | undefined>(this, undefined);
  @property({ attribute: false }) examDays = new RXController<ILSUserOverviewLineOrder[] | undefined>(this, undefined);

  override render() {
    return html`
      <x-flex horizontal space="md">
        ${this._orderDetails()}
        ${permissionGuard({ resource: 'orders', action: 'createmanual', guardedTemplate: this._addOrder() })}
      </x-flex>
    `;
  }

  private _addOrder(): Renderable {
    return squareIcon(this, 'add', {
      size: '1.4rem',
      isSelected: true,
      onClick: async () => {
        const result = await ILSAdminCreateOrder.showPopup<Maybe<Order>>({
          props: {
            orderSelector,
            user: this.user.value!,
          },
        });

        if (result instanceof Order) {
          void XSnackbar.show(
            `Kauf von Kunde ${this.user.value!.ecommerce?.fullName ?? this.user.value!.username} manuell eingetragen.`,
          );
          this.dispatchEvent(new CustomEvent('update-required'));
        }
      },
    });
  }

  private _orderDetails() {
    const dayIDsWithOrders = this.examDays.value?.reduce((acc, day) => {
      if (day.examDayID) acc.add(day.examDayID);
      return acc;
    }, new Set<string>());
    if (!dayIDsWithOrders?.size) return nothing;
    return squareIcon(this, 'read_more', {
      size: '1.4rem',
      isSelected: true,
      onClick: async () => {
        await ILSAdminOrderDetailContent.showPopup({
          props: {
            user: this.user.value,
            examDays: this.examDays.value,
          },
          modal: true,
        });
        this.dispatchEvent(new CustomEvent('update-required'));
      },
      slotted: html` <x-text> ${dayIDsWithOrders.size} </x-text> `,
    });
  }
}

/**
 * @element ils-admin-order-detail-content
 */
@customElement('ils-admin-order-detail-content')
export class ILSAdminOrderDetailContent extends XDialog<void> {
  @state() private readonly _currentUser = new RXController(this, currentUser<ILSUser>());
  @property({ attribute: false }) examDays = new RXController<ILSUserOverviewLineOrder[] | undefined>(this, undefined);
  @property({ attribute: false }) user = new RXController<ILSUser | undefined>(this, undefined);
  // prettier-ignore
  @property({ attribute: false }) daysWithOrders = new RXController<Observable<Array<{ day: NarrowExamDay; orders: NarrowOrder[] }>>>(
    this,
    combineLatest([
      this.examDays.observable.pipe(switchMap(days => days ? subscribeByID(ExamDay, days.map(day => day.examDayID))(examDaySelector) : of([]))),
      this.user.observable.pipe(
        filter(Boolean),
        switchMap(user => subscribeOrdersByUserID(user._id)(orderSelector)),
      ),
    ]).pipe(
      map(
        ([days, orders]) =>
          days.map(day => {
            const dayOrders = orders.filter(o => o.products[0]?.product.reference === day._id);
            return { day, orders: dayOrders };
          })
          .filter(dayWithOrders => dayWithOrders.orders.length),
      ),
    ),
  );

  @property({ attribute: false }) selectedDay = new RXController<
    { day: NarrowExamDay; orders: NarrowOrder[] } | undefined
  >(this, undefined);

  @state()
  private readonly _selectedOrder = new RXController<Maybe<NarrowOrder>>(this, undefined);

  @state() private readonly _selectedOrderSubscription = new RXController<Observable<Maybe<NarrowOrder>>>(
    this,
    this._selectedOrder.observable.pipe(
      switchMap(order => {
        if (!order) return of(null);
        return subscribeByID(Order, order._id)(orderSelector).pipe(startWith(order));
      }),
    ),
  );

  @state() private readonly _orderChanging = new RXController(this, this._selectedOrder.observable, order$ =>
    order$.pipe(switchMap(order => order?.whenChanging ?? of(null))),
  );

  static override get element_name() {
    return 'ils-admin-order-detail-content';
  }

  override willUpdate(changedProps: PropertyValues) {
    super.willUpdate(changedProps);

    if (!this.selectedDay.value && this.daysWithOrders.value?.length)
      this.selectedDay.next(this.daysWithOrders.value[0]);

    if ((!this._selectedOrder.value || changedProps.has('selectedDay')) && this.selectedDay.value?.orders?.length)
      this._selectedOrder.next(
        this.selectedDay.value
          ? this.selectedDay.value.orders
              .slice(1)
              .reduce(
                (acc, o) => (compareArr.indexOf(acc.status) > compareArr.indexOf(o.status) ? acc : o),
                this.selectedDay.value.orders[0]!,
              )
          : undefined,
      );
  }

  override render() {
    return html`
      <x-basic-card
        raised
        horizontal
        ${css({
          background: this.colors.light,
          borderRadius: this.sizes.borderRadius,
        })}
      >
        <x-flex ${css({ borderRight: `2px solid ${this.colors.neutral.shade(100)}` })}>
          ${repeat(
            this.daysWithOrders.value ?? [],
            day => day.day._id,
            dayWithOrders => {
              const isExpanded = this.selectedDay.value?.day._id === dayWithOrders.day._id;
              return html`
                <x-flex
                  horizontal
                  space="xs"
                  crossaxis-center
                  padding="sm"
                  ${css({
                    cursor: 'pointer',
                    userSelect: 'none',
                    background: isExpanded ? this.colors.neutral.shade(100) : this.colors.light,
                  })}
                  @click=${() => this.selectedDay.next(dayWithOrders)}
                >
                  <x-icon> ${!isExpanded ? 'expand_more' : 'expand_less'} </x-icon>
                  <x-text flex> ${dayWithOrders.day.start.toLocaleString(DateTime.DATETIME_SHORT)} </x-text>
                  ${examTypeBadge(dayWithOrders.day.examType, null, true)}
                </x-flex>
                ${isExpanded
                  ? html`
                      <x-flex horizontal padding="xs" wrap space="sm" ${css({ width: '200px' })}>
                        ${repeat(
                          this.selectedDay.value?.orders ?? [],
                          o => o._id,
                          dayOrder => html`
                            ${orderStatusIcon(this as any, dayOrder.status, {
                              title: dayOrder.status + ' ' + dayOrder.createdAt.toLocaleString(DateTime.DATETIME_SHORT),
                              isSelected: dayOrder === this._selectedOrder.value,
                              onClick: () => this._selectedOrder.next(dayOrder),
                            })}
                          `,
                        )}
                      </x-flex>
                    `
                  : nothing}
              `;
            },
          )}
        </x-flex>
        ${this._detail()}
      </x-basic-card>
    `;
  }

  private _detail() {
    if (!this._selectedOrderSubscription.value || !this.selectedDay.value)
      return html`
        <x-flex padding="lg">
          <ils-loader> ${this._selectedOrderSubscription.value} ${this.selectedDay.value} </ils-loader>
        </x-flex>
      `;
    const order = this._selectedOrderSubscription.value;
    const selectedDay = this.selectedDay.value;

    return html`
      <x-flex h100 space="lg" style="width: 420px;">
        <x-flex flex space="md" padding="sm">
          <x-flex horizontal crossaxis-center spacing="md">
            <x-text bold flex>
              ${this.user.value?.ecommerce
                ? this.user.value.ecommerce.firstName + ' ' + this.user.value.ecommerce.lastName
                : this.user.value?.username}
            </x-text>
            <x-button mode="icon" icon="close" @click=${() => this.close()}> </x-button>
          </x-flex>
          ${this._orderInfo(order, selectedDay)}
        </x-flex>
        <x-grid
          columns="2"
          ${css({
            background: this.colors.neutral.shade(100),
            gap: '1px',
            borderTop: `2px solid ${this.colors.neutral.shade(100)}`,
            borderBottom: `2px solid ${this.colors.neutral.shade(100)}`,
          })}
        >
          ${permissionGuard({
            resource: 'orders',
            action: 'revoke',
            data: order,
            guardedTemplate: this._revokeInvoiceButton(order),
          })}
          ${permissionGuard({
            resource: 'orders',
            action: 'move',
            data: order,
            guardedTemplate: this._moveAppointmentButton(order),
          })}
          ${permissionGuard({
            resource: 'orders',
            action: 'setpaid',
            data: order,
            guardedTemplate: this._setPaidButton(order),
          })}
          ${permissionGuard({
            resource: 'orders',
            action: 'modifycomment',
            data: order,
            guardedTemplate: this._addOrderComment(order),
          })}
          ${permissionGuard({
            resource: 'invoices',
            action: 'view',
            data: order,
            guardedTemplate: this._showInvoiceButton(order),
          })}
          ${permissionGuard({
            resource: 'invoices',
            action: 'create',
            data: order,
            guardedTemplate: this._newInvoiceButton(order),
          })}
          ${permissionGuard({
            resource: 'results',
            action: 'view',
            data: order,
            guardedTemplate: this._resultButton(order, this.user.value),
          })}
          ${permissionGuard({
            resource: 'certificates',
            action: 'sendmail',
            data: order,
            guardedTemplate: this._certificateButton(order, this.user.value),
          })}
          ${permissionGuard({
            resource: 'invoices',
            action: 'view',
            data: order,
            guardedTemplate: this._mailPreview(),
          })}
        </x-grid>
      </x-flex>
    `;
  }

  private _orderInfo(order: NarrowOrder, selectedDay: { day: NarrowExamDay; orders: NarrowOrder[] }) {
    const rows = [
      ['Buchungsdatum:', order.createdAt.toLocaleString(DateTime.DATETIME_SHORT)],
      ['Prüfungstag:', selectedDay.day.start.toLocaleString(DateTime.DATETIME_SHORT)],
      ['Prüfungstyp:', selectedDay.day.examType],
      ['Standort:', selectedDay.day.location?.displayName ?? '-'],
      ['Status:', order.status],
      ...this._movedOrderRows(order),
      // @ts-expect-error we explicitly want type manual here
      ['StripeID:', order.paymentType === 'manual' ? 'manual' : order.processorInfo.stripePaymentIntent ?? '-'],
      ...this._revokedOrderRows(order),
      [
        html` <x-text style="padding: 3px 0;"> Kommentar: </x-text> `,
        html`
          <div style="width: 220px; word-wrap: break-word; overflow-y: auto; max-height: calc(14px * 3 * 1.4)">
            <x-text style="padding: 3px 0;">${this._selectedOrderSubscription.value?.comment || '-'}</x-text>
          </div>
        `,
      ],
      ...this._devDebugRows(order),
    ] as Array<[Renderable, Renderable]>;
    return simpleInformationGrid(rows);
  }

  private _movedOrderRows(order: NarrowOrder): Array<[Renderable, Renderable]> {
    if (order.status !== 'moved' && !order.movedFromOrder) return [];
    if (order.status === 'moved') {
      const targetDayWithOrders = this.daysWithOrders.value?.find(dayWithOrders =>
        dayWithOrders.orders.find(o => o.movedFromOrder === order._id),
      );
      if (!targetDayWithOrders) return [['Moved', 'cannot find target day?']];
      return [['Verschoben nach', targetDayWithOrders.day.start.toLocaleString(DateTime.DATETIME_SHORT)]];
    }
    if (order.movedFromOrder) {
      const sourceDayWithOrders = this.daysWithOrders.value?.find(dayWithOrders =>
        dayWithOrders.orders.find(o => o._id === order.movedFromOrder),
      );
      if (!sourceDayWithOrders) return [['Moved', 'cannot find source day?']];
      return [['Verschoben von', sourceDayWithOrders.day.start.toLocaleString(DateTime.DATETIME_SHORT)]];
    }
    return [['Moved', 'error case: should not happen']];
  }

  private _revokedOrderRows(order: NarrowOrder): Array<[Renderable, Renderable]> {
    if (order.status !== 'revoked') return [];
    return [
      [
        'Storniert am:',
        order.revokedAt ? DateTime.fromJSDate(order.revokedAt).toLocaleString(DateTime.DATETIME_SHORT) : '-',
      ],
      ['StornierungsID:', order.processorInfo.stripeRefundID ?? '-'],
    ];
  }

  private _devDebugRows(order: NarrowOrder): Array<[Renderable, Renderable]> {
    if (!isDevelopment) return [];
    return [
      [
        html`<x-text style="padding: 3px 0;"><b>DEV*</b> Order ID:</x-text>`,
        html`<x-text>${this._selectedOrderSubscription.value?._id ?? 'KEINE ORDER ID'}</x-text>`,
      ],
      [
        html`<x-text style="padding: 3px 0;"><b>DEV*</b> Invoice Number:</x-text>`,
        html`<x-text>${order.invoiceNumber ?? 'KEINE INVOICE ID'}</x-text>`,
      ],
      [
        html`<x-text style="padding: 3px 0;"><b>DEV*</b> Order Number</x-text>`,
        html`<x-text>${order.orderNumber ?? 'KEINE ORDER NUMBER'}</x-text>`,
      ],
    ];
  }

  private async _revokeInvoice(order: NarrowOrder) {
    return XNativeDialog.guard(
      () =>
        manuallySetOrderRevoked(order._id)().then(() => {
          // optimistically update order
          order.status = 'revoked';
          order.revokedAt = new Date() as any;
          this.requestUpdate();

          return XSnackbar.show('Bestellung erfolgreich storniert');
        }),
      {
        confirmText: `Bestellung unwiderruflich stornieren?`,
        hasToConfirm: true,
        showLoaderModal: true,
      },
    );
  }

  private _addOrderComment(order: NarrowOrder) {
    return this._button({
      text: 'Kommentar',
      icon: '',
      onClick: () => {
        void ILSOrderCommentForm.showPopup({ modal: true, props: { order } });
      },
    });
  }

  // "Stornieren"
  private _revokeInvoiceButton(order: NarrowOrder) {
    if (!hasPermission(this._currentUser.value, 'orders', 'revoke', order)) return nothing;
    if (order.status !== 'paid') return nothing;
    return this._button({
      text: 'Stornieren',
      icon: 'event_busy',
      onClick: () => {
        void XNativeDialog.showPopup({
          modal: true,
          content: (resolve, reject) => html`
            <x-flex padding="md" space="md">
              <x-h1> Order Stornieren </x-h1> <x-text> Wollen Sie die Order wirklich stornieren? </x-text>
              <x-h4>User</x-h4>
              <x-grid columns="max-content max-content" space="sm" style="align-self:start;">
                <x-text>Name: </x-text>
                <x-text>${this.user.value?.ecommerce?.fullName}</x-text>
                <x-text>E-Mail: </x-text>
                <x-text>${this.user.value?.email}</x-text>
              </x-grid>
              <x-h4>Order Details</x-h4>
              ${this._orderInfo(order, this.selectedDay.value)}
              <x-flex horizontal space="md" space-between>
                <x-button mode="outline" @click=${() => reject()}> Abbrechen </x-button>

                <x-button
                  @click=${async () => {
                    await this._revokeInvoice(order);
                    resolve(true);
                  }}
                >
                  Bestätigen
                </x-button>
              </x-flex>
            </x-flex>
          `,
        });
      },
    });
  }

  private _moveAppointmentButton(order: NarrowOrder) {
    if (order.status !== 'paid') return nothing;
    return this._button({
      text: 'Verschieben',
      icon: 'free_cancellation',
      onClick: () =>
        ILSAdminMoveOrder.showPopup({
          props: {
            orderSelector,
            user: this.user.value,
            order: this._selectedOrderSubscription.value,
          },
        }),
    });
  }

  private _showInvoiceButton(order: NarrowOrder) {
    if (!['paid', 'moved'].includes(order.status)) return nothing;
    return this._button({
      text: 'Rechnung',
      icon: 'receipt_long',
      onClick: async () => {
        if (order.invoices[0]?.reference) {
          const invoice = await getByID(ILSFile, order.invoices[0].reference)(ILSFile.allFields);
          // todo: replace url with PdfUtil function
          if (invoice) goTo(`${invoice.getDownloadLink()}/${order.invoiceNumber}.pdf`, { target: '_blank' });
          else
            void XSnackbar.show('Rechnung ist kaputt gegangen, bitte neu generieren und der Technik Bescheid sagen. ');
        } else if (order.movedFromOrder) {
          void XNativeDialog.showPopup({
            modal: true,
            content: resolve => html`
              <x-flex padding="md" space="md">
                <x-text>
                  Diese Order wurde verschoben. Rechnung bitte über alte Order öffnen, oder neue generieren.
                </x-text>
                <x-flex horizontal end space="md">
                  <x-button @click=${() => resolve({})}> OK </x-button>
                </x-flex>
              </x-flex>
            `,
          });
        } else {
          void XNativeDialog.showPopup({
            modal: true,
            content: resolve => html`
              <x-flex padding="md" space="md">
                <x-text> Es gibt noch keine Rechnung. </x-text>
                <x-flex horizontal end space="md">
                  <x-button
                    mode="outline"
                    icon="add"
                    @click=${async (e: CustomEvent) => {
                      const button = e.currentTarget as XButton;
                      button.loading = true;
                      try {
                        const invoiceID = await createInvoice(order._id)();
                        if (!invoiceID) throw new Error('Beim erstellen der Rechnung ist etwas schief gelaufen.');
                        goTo(generateInvoicePdfFileName(order, invoiceID), { target: '_blank' });
                      } catch (e: any) {
                        logger.error(e);
                        void XNativeDialog.alert(e.message);
                      }
                      button.loading = false;
                      resolve({});
                    }}
                  >
                    Neue Rechnung generieren
                  </x-button>
                  <x-button @click=${() => resolve({})}> OK </x-button>
                </x-flex>
              </x-flex>
            `,
          });
        }
      },
    });
  }

  private _newInvoiceButton(order: NarrowOrder) {
    if (order.status !== 'paid') return nothing;
    return this._button({
      text: 'Neue Rechnung',
      icon: 'edit_document',
      onClick: async e => {
        if (!e) return;

        const button = e.currentTarget as XButton;
        button.loading = true;

        await openPopupWithLoadingScreen(
          new Promise<string>(async (resolve, reject) => {
            const invoiceID = await recreateInvoice(order._id)();
            if (!invoiceID) return reject(new Error('Beim erstellen der Rechnung ist etwas schief gelaufen.'));

            // not pretty but it works, ideally the Order should be a subscription
            // is neccessary to guarantee an invoiceNumber
            const updatedOrder = await getByID(Order, order._id)(orderSelector);
            if (updatedOrder) order = updatedOrder;

            const url = generateRecreateInvoicePdfFileName(order, invoiceID);
            resolve(url);
          }),
          {},
        );

        button.loading = false;
      },
    });
  }

  private _certificateButton(order: NarrowOrder, user: ILSUser) {
    if (!order.isExamDayPassed) return nothing;
    return this._button({
      text: 'Urkunde',
      // tooltipText: 'Urkunden sind gerade nicht aktiviert und werden manuell über VirtualBadge.io gehandhabt',
      icon: 'school',
      onClick: async () => {
        // TODO merge this logic with the send certificate button in ils-evaluation-overview
        if (
          !(await XNativeDialog.confirm(
            'Es wird eine Urkunde via VirtualBadge.io erstellt und an den Teilnehmer gesendet. Fortfahren?',
          ))
        )
          return;
        await XNativeDialog.waitFor(
          sendCertificate(order._id, this.selectedDay.value?.day.start.toFormat('dd.MM.yyyy') ?? '')(),
          'Generiere Urkunde...',
        );
      },
    });
  }

  private _resultButton(order: NarrowOrder, user: ILSUser) {
    if (!order.isExamDayRated) return nothing;
    return this._button({
      text: 'Ergebnis',
      icon: 'fact_check',
      onClick: async () => {
        // TODO merge this logic with the send results button in ils-evaluation-overview
        void XNativeDialog.showPopup<void>({
          content: resolve =>
            html`
              <x-flex space="lg" padding="md">
                <x-flex space="md" crossaxis-center horizontal>
                  <x-button
                    mode="outline"
                    @click=${async () => {
                      const link = await XNativeDialog.waitFor(
                        servePdfFromComponent(
                          'print-result',
                          `order=${order._id}`,
                          generateResultPdfFileName(this.user.value, this.selectedDay.value.day),
                        )(),
                        'Generiere Ergebnis...',
                      );
                      if (isDevelopment) console.debug('DEV Log:', generatePdfUrl(order, 'result'));
                      goTo(link, { target: '_blank' });
                      resolve();
                    }}
                  >
                    Ergebnis anschauen / drucken
                  </x-button>
                  ${permissionGuard({
                    resource: 'results',
                    action: 'sendmail',
                    guardedTemplate: html`
                      <x-button
                        @click=${async () => {
                          const successful = await XNativeDialog.waitFor(
                            ExamDay.sendRatingResultMail(order._id)()
                              .then(() => true)
                              .catch(e => XNativeDialog.alert(e.message)),
                          );
                          if (successful) {
                            resolve();
                            await XSnackbar.show('Ergebnis erfolgreich an Prüfling gesendet');
                          }
                        }}
                      >
                        Ergebnis an Prüfling senden
                      </x-button>
                    `,
                  })}
                </x-flex>
                <x-button mode="outline" ${css({ alignSelf: 'flex-end' })} @click=${resolve}> Abbrechen </x-button>
              </x-flex>
            `,
        });
      },
    });
  }

  private _setPaidButton(order: NarrowOrder) {
    if (order.status !== 'issued') return nothing;
    return this._button({
      text: 'Auf bezahlt setzen',
      icon: 'check',
      columns: 2,
      onClick: () =>
        XNativeDialog.waitFor(
          manuallySetOrderPaid(order._id)()
            .catch(e => XNativeDialog.alert(e.message))
            .then(() => XSnackbar.show('Bestellung erfolgreich auf bezahlt gesetzt')),
        ),
    });
  }

  // dev only
  private _mailPreview() {
    if (!isDevelopment) return nothing;
    return this._button({
      text: 'Rechnung Preview (DEV)',
      icon: 'mail',
      onClick: () => window.open(`/__print/print-invoice?order=${this._selectedOrderSubscription.value._id}`, '_blank'),
    });
  }

  private _button({
    text,
    onClick,
    icon,
    columns,
    tooltipText,
  }: {
    text: string;
    tooltipText?: string;
    onClick?: (e?: MouseEvent) => any;
    icon?: string;
    columns?: number;
  }) {
    return html`
      <x-flex
        flex
        center
        crossaxis-center
        horizontal
        space="sm"
        padding="sm"
        ${acss({
          cursor: 'pointer',
          background: this.colors.neutral.shade(50),
          color: this.colors.neutral,
          ...(columns ? { gridColumn: `span ${columns}` } : {}),
          '&:hover': { background: this.colors.neutral.shade(100) },
        })}
        @click=${(e: MouseEvent) => onClick?.(e)}
      >
        ${icon ? html` <x-icon> ${icon} </x-icon> ` : nothing}
        <x-text tone="subtle"> ${text} </x-text>
        ${tooltipText ? html` <x-tooltip .text=${html` <x-text> ${tooltipText} </x-text> `}></x-tooltip> ` : nothing}
      </x-flex>
    `;
  }
}
